import type { Ref } from 'vue'

interface TimeAgoTranslations {
  justNow: string
  minutesAgo: string
  minuteAgo: string
  hoursAgo: string
  hourAgo: string
}

const timeAgoTranslations = {
  en: {
    justNow: 'Just now',
    minutesAgo: '{count} minutes ago',
    minuteAgo: '1 minute ago',
    hoursAgo: '{count} hours ago',
    hourAgo: '1 hour ago'
  },
  de: {
    justNow: 'Gerade eben',
    minutesAgo: 'Vor {count} Minuten',
    minuteAgo: 'Vor 1 Minute',
    hoursAgo: 'Vor {count} Stunden',
    hourAgo: 'Vor 1 Stunde'
  }
}

/**
 * Formats a date string into a human-readable "time ago" format
 * @param dateString - ISO date string to format
 * @param locale - Language locale ('en' or 'de'), defaults to 'en'
 * @returns Formatted time ago string
 */
export function formatTimeAgo(dateString: string, locale: 'en' | 'de' = 'en'): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60))

  const translations = timeAgoTranslations[locale] || timeAgoTranslations.en

  if (diffInMinutes < 1) {
    return translations.justNow
  } else if (diffInMinutes < 60) {
    const template = diffInMinutes === 1 ? translations.minuteAgo : translations.minutesAgo
    return template.replace('{count}', diffInMinutes.toString())
  } else if (diffInMinutes < 1440) {
    // 24 hours
    const hours = Math.floor(diffInMinutes / 60)
    const template = hours === 1 ? translations.hourAgo : translations.hoursAgo
    return template.replace('{count}', hours.toString())
  } else {
    // For dates older than 24 hours, show the actual date
    return date.toLocaleDateString(locale === 'de' ? 'de-DE' : 'en-US')
  }
}

/**
 * Composable function that provides a reactive formatTimeAgo function
 * that uses the provided locale
 */
export function useTimeFormatting(locale: Ref<string>) {
  const formatTimeAgoLocalized = (dateString: string): string => {
    return formatTimeAgo(dateString, locale.value as 'en' | 'de')
  }

  return {
    formatTimeAgo: formatTimeAgoLocalized
  }
}
